import { useEffect, useState } from 'react';
import { useJobTaskApi } from '../../../clients/quipli-service/job-tasks/job-task-api';
import { JobType, Job, TaskType, JobConfig } from '../../../clients/quipli-service/job-tasks/job-tasks';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import { Order } from '../../../clients/quipli-service/order/order';
import Icon from '../../../components/icons/icon';
import { Button } from '../../../components/form/button';
import { Product, ProductInventoryUnit } from '../../../clients/quipli-service/product/product';
import { Modal } from '../../../components/modal/modal';
import { InspectionModalContent } from './inspection-modal/inspection-modal';
import { OrderSection } from '../../order/order-section/order-section';
import { toast } from '../../../util/toasts';
import { useOrderMedia } from '../../order/hooks/use-order-media';
import { UnitInspectionView } from './components/unit-inspection-view/unit-inspection-view';

/**
 * Inspection view for a single order.
 * @param order - The order to inspect.
 * @param jobType - The type of job to create.
 * @param unitTrackingVia - The unit tracking via to use.
 * @param fuelUnit - The fuel unit to use.
 * @param fetchOrder - The function to fetch the order.
 * @returns The inspection view.
 */
export const InspectionView = ({
  order,
  jobType,
  unitTrackingVia,
  fuelUnit,
  fetchOrder,
}: {
  order: Order;
  jobType: JobType;
  unitTrackingVia: 'serialNumber' | 'unitNumber';
  fuelUnit: 'gallons' | 'liters';
  fetchOrder: () => void;
}) => {
  const { getJobByEntity, startJob, getProductJobConfigs } = useJobTaskApi();
  const [job, setJob] = useState<Job>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showInspectionSummary, setShowInspectionSummary] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] = useState<ProductInventoryUnit>();
  const [productToJobConfigMap, setProductToJobConfigMap] = useState<Record<number, JobConfig[]>>({});

  const { isOrderMediaLoading, mediaList } = useOrderMedia(order);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const job = await getJobByEntity('Order', `${order.id}`, jobType);
      setJob(job);

      const productJobConfigsArr = await Promise.all(
        order.products.flatMap(async (p) => {
          const configs = await getProductJobConfigs(jobType, p.product.id);
          return { productId: p.product.id, configs };
        }),
      );

      // Build the map: productId -> configs[]
      const productToJobConfigMap: Record<number, JobConfig[]> = {};
      for (const { productId, configs } of productJobConfigsArr) {
        productToJobConfigMap[productId] = configs;
      }

      setProductToJobConfigMap(productToJobConfigMap);
    } catch (e) {
      console.error(e);
      toast('Failed to fetch job', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJob();
  }, []);

  const initInspection = async () => {
    try {
      setLoading(true);
      const job = await startJob(order.id, jobType);
      setJob(job);
      setSelectedUnit(
        order.products
          .find((_) =>
            _.assignedUnits.some(
              (u) => u.id === job.tasks.find((_) => _.metadata?.productUnitId)?.metadata?.productUnitId,
            ),
          )
          ?.assignedUnits.find(
            (u) => u.id === job.tasks.find((_) => _.metadata?.productUnitId)?.metadata?.productUnitId,
          ),
      );
    } catch (e) {
      console.error(e);
      toast('Failed to start inspection', 'error');
    } finally {
      setLoading(false);
    }
  };

  const doesProductHaveJobConfig = (product: Product) => {
    const configs = productToJobConfigMap[product.id];
    return configs && configs.length > 0;
  };

  return (
    <OrderSection>
      <div className="inspection-view">
        {loading || isOrderMediaLoading ? (
          <div className="flex justify-between items-center">
            <Typography type="text-md" weight="medium" className="text-grey-800">
              {jobType === JobType.CheckoutInspection ? 'Check out Inspection' : 'Check in Inspection'}
            </Typography>
            <Spinner />
          </div>
        ) : !job && order.products.some((p) => doesProductHaveJobConfig(p.product)) ? (
          <div className="flex justify-between items-center">
            <Typography type="text-md" weight="medium" className="text-grey-800">
              {jobType === JobType.CheckoutInspection ? 'Check out Inspection' : 'Check in Inspection'}
            </Typography>
            <Button
              disabled={loading || isOrderMediaLoading}
              className="button button-yellow testing1"
              onClick={() => initInspection()}
            >
              Start
            </Button>
          </div>
        ) : job && job.tasks?.length > 0 ? (
          <div>
            <div
              className="mb-3 inspection-header-collapsible-trigger flex justify-between items-center cursor-pointer"
              onClick={() => setShowInspectionSummary(!showInspectionSummary)}
            >
              <Typography type="text-md" weight="medium" className="text-grey-800">
                {jobType === JobType.CheckoutInspection ? 'Check out Inspection' : 'Check in Inspection'}
              </Typography>
              {job.tasks?.length ? (
                <JobHeader job={job} showInspectionSummary={showInspectionSummary} />
              ) : (
                <Button
                  disabled={loading || isOrderMediaLoading}
                  className="button button-yellow testing2"
                  onClick={() => initInspection()}
                >
                  Start
                </Button>
              )}
            </div>
            {/* begin inspection summary stuff */}
            {showInspectionSummary &&
              order.products
                .filter((orderProduct) => doesProductHaveJobConfig(orderProduct.product))
                .flatMap((orderProduct) =>
                  orderProduct.assignedUnits.map((unit) => {
                    return (
                      <UnitInspectionView
                        key={unit.id}
                        inspection={job}
                        orderProduct={orderProduct}
                        unit={unit}
                        unitTrackingVia={unitTrackingVia}
                        fuelUnit={fuelUnit}
                        fetch={fetchOrder}
                        setSelectedUnit={setSelectedUnit}
                        jobType={jobType}
                        mediaList={mediaList}
                      />
                    );
                  }),
                )}

            {selectedUnit !== undefined && (
              <Modal
                className="inspection-modal"
                isOpen={selectedUnit !== undefined}
                close={() => setSelectedUnit(undefined)}
              >
                <InspectionModalContent
                  unitTrackingVia={unitTrackingVia}
                  fuelUnit={fuelUnit}
                  unit={selectedUnit!}
                  job={job}
                  product={order.products.find((p) => p.assignedUnits.some((u) => u.id === selectedUnit?.id))!.product}
                  close={() => {
                    setSelectedUnit(undefined);
                    fetchJob().then(() => fetchOrder());
                  }}
                  setJob={setJob}
                  order={order}
                />
              </Modal>
            )}
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <Typography type="text-md" weight="medium" className="text-grey-800">
              {jobType === JobType.CheckoutInspection ? 'Check out Inspection' : 'Check in Inspection'}
            </Typography>
            <Typography type="text-sm" className="text-grey-500">
              Not required
            </Typography>
          </div>
        )}
      </div>
    </OrderSection>
  );
};

const JobHeader = ({ job, showInspectionSummary }: { job: Job; showInspectionSummary: boolean }) => {
  return (
    <div
      className={`job-header flex items-center gap-2 ${
        job.status === 'Complete'
          ? job.tasks.find(
              (t) =>
                t.type === TaskType.InspectionChecklist &&
                t.metadata?.checklist.some((item: any) => item.value === false),
            )
            ? 'text-red-500'
            : 'text-green-500'
          : job.status === 'InProgress'
          ? 'text-yellow-500'
          : 'text-grey-500'
      }`}
    >
      {job.status === 'Complete' &&
        job.tasks.find(
          (t) =>
            t.type === TaskType.InspectionChecklist && t.metadata?.checklist.some((item: any) => item.value === false),
        ) && <Icon icon="CircleExclamation" stroke="yellow" />}
      <Typography type="text-sm" weight="semi-bold">
        {job.status === 'NotStarted'
          ? 'Not Started'
          : job.status === 'InProgress'
          ? 'In Progress'
          : `Complete${
              job.tasks.find(
                (t) =>
                  t.type === TaskType.InspectionChecklist &&
                  t.metadata?.checklist.some((item: any) => item.value === false),
              )
                ? ' - Not Passed'
                : ''
            }`}
      </Typography>
      <Icon className="job-header-icon cursor-pointer" icon={showInspectionSummary ? 'ChevronUp' : 'ChevronDown'} />
    </div>
  );
};
